import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";

import { FieldTypeIcon } from "@/shared/components/filters/fieldTypeIcons";
import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import {
  effectivePresets,
  type Filter,
  filterSchema,
  getDefaultsForVariant,
  isValueless,
  normalizeForDefinition,
  pickerComparators,
  withComparator,
} from "@/shared/components/filters/FilterSchema";
import { FilterValueInput } from "@/shared/components/filters/FilterValueInput";
import { Button } from "@/shared/foundation/primitives/Button";

import styles from "./FilterForm.module.css";
import { OperatorPicker } from "./OperatorPicker";

interface FilterFormProps {
  /** The field the user picked in the left pane. */
  selectedField: FilterDefinition;

  /** Fired when the user clicks Add filter (or Cmd/Ctrl+Enter on a non-text input). */
  onCommit: (filter: Filter) => void;

  /** Fired when the user clicks Cancel. */
  onCancel: () => void;

  /**
   * Seeds the form with an existing filter's comparator and values (the
   * chip-edit flow). Must match `selectedField`'s type/field. When absent,
   * the form starts from the field's blank defaults (the add flow).
   */
  initialFilter?: Filter;

  /** Submit button label. Defaults to `"Add filter"`; the edit popover
   *  passes `"Update filter"`. */
  submitLabel?: string;
}

/**
 * The right pane of the filter menu — and the entire form.
 */
export function FilterForm({
  selectedField,
  onCommit,
  onCancel,
  initialFilter,
  submitLabel = "Add filter",
}: FilterFormProps) {
  // A stored filter opens in the form this field's picker offers, where one
  // says the same thing (an enum `EQUALS`, a nullable boolean's `EQUALS`).
  const [initialValues] = React.useState(() =>
    initialFilter
      ? normalizeForDefinition(initialFilter, selectedField)
      : getDefaultsForVariant(selectedField),
  );
  const form = useForm<Filter>({
    resolver: zodResolver(filterSchema),
    defaultValues: initialValues,
    // Continuous validation so the Add filter button reflects validity
    // live as the user fills the value input(s).
    mode: "onChange",
  });

  const filter = form.watch();
  // Text typed into a list of values that could be added. It is added when
  // focus leaves the input — as it does for this form's submit button — so it
  // counts toward submitting before it's in `values`.
  const [hasValidDraft, setHasValidDraft] = React.useState(false);

  const setIncludeNull = (includeNull: boolean) =>
    form.setValue("includeNull", includeNull, {
      shouldValidate: true,
      shouldDirty: true,
    });
  // Clear all / Select all change "Not set" with the values, in one call.
  const setValues = (next: Filter["values"], includeNull?: boolean) => {
    form.setValue("values", next, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (includeNull !== undefined) {
      setIncludeNull(includeNull);
    }
  };

  return (
    <form
      // No filter fields need browser provided autocomplete
      autoComplete="off"
      className={styles.panel}
      onSubmit={(event) => {
        void form.handleSubmit((values) => onCommit(values))(event);
      }}
    >
      <section className={styles.content}>
        <FilterFormHeader field={selectedField} />
        <section className={styles.section} aria-label="Operator">
          <h4 className={styles.sectionLabel}>Operator</h4>
          <Controller
            name="comparator"
            control={form.control}
            render={({ field }) => (
              <OperatorPicker
                fieldType={selectedField.type}
                value={field.value}
                onChange={(next) => {
                  // Read before the comparator changes: what survives
                  // depends on the operator being left.
                  const current = form.getValues();
                  const nextFilter = withComparator(current, next);
                  // Leaving "is any of" drops its "Not set" member, which
                  // no other operator can hold.
                  if (
                    "includeNull" in current &&
                    !("includeNull" in nextFilter)
                  ) {
                    form.unregister("includeNull");
                  }
                  field.onChange(next);
                  setValues(nextFilter.values);
                }}
                // Read off the filter the form opened with, so a stored
                // operator this field no longer offers stays listed after
                // the author moves off it.
                operators={pickerComparators(
                  selectedField,
                  initialValues.comparator,
                )}
                presets={effectivePresets(selectedField)}
              />
            )}
          />
        </section>
        {!isValueless(filter.comparator) && (
          <section
            className={classNames(styles.section, styles.valueSection)}
            aria-label="Value"
          >
            <h4 className={styles.sectionLabel}>Value</h4>
            <FilterValueInput
              filter={filter}
              definition={selectedField}
              onChange={setValues}
              onIncludeNullChange={setIncludeNull}
              onDraftValidChange={setHasValidDraft}
            />
          </section>
        )}
      </section>
      <div className={styles.footer}>
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={onCancel}
          className={styles.footerAction}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={!form.formState.isValid && !hasValidDraft}
          className={styles.footerAction}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function FilterFormHeader({ field }: { field: FilterDefinition }) {
  return (
    <header className={styles.header}>
      <div className={styles.headerGroup}>
        {field.icon ? (
          <span className={styles.headerIcon}>{field.icon}</span>
        ) : (
          <FieldTypeIcon type={field.type} className={styles.headerIcon} />
        )}
        <h3 className={styles.headerName}>{field.name}</h3>
      </div>
      <span className={styles.headerType}>{field.type}</span>
    </header>
  );
}
